import React, { useEffect, useImperativeHandle, useState, ForwardRefRenderFunction } from "react";
import Modal from "react-modal";
import { postData, putData, uploadImage, uploadvideo } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { useLoader } from "../../components/loader/loader";
import messageService from "../../services/message/messageService";
import { showAPiError_mesage } from "../../services/validationService/validationservice";
import { ImagePlus, X, XCircle } from "lucide-react";

interface Props {
  isOpen: boolean;
  openModal: any;
  closeModal: () => void;
  getDataALLs?: () => void;
}

const AddVideoModal: ForwardRefRenderFunction<any, Props> = (props, ref) => {
  const { isOpen, openModal, closeModal, getDataALLs } = props;
  const { show, hide } = useLoader();
  const [formData, setFormData] = useState({ title: "", thumbnail: "", videoUrl: "" });
  const [thumbnail, setThumbnail] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (openModal) {
      setFormData({
        title: openModal?.title || "",
        thumbnail: openModal?.thumbnail || "",
        videoUrl: openModal?.videoUrl || "",
      });
      setThumbnail(openModal?.thumbnail || "");
      setVideoUrl(openModal?.videoUrl || "");
    } else {
      resetForm();
    }
  }, [openModal]);

  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  const resetForm = () => {
    setFormData({ title: "", thumbnail: "", videoUrl: "" });
    setThumbnail("");
    setVideoUrl("");
    setError({});
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!thumbnail) newErrors.thumbnail = "Thumbnail is required";
    if (!videoUrl) newErrors.videoUrl = "Video is required";
    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const payload = { ...formData, thumbnail, videoUrl, _id: openModal?._id };
      const url = openModal?._id ? ApiUrl.updateVideo + openModal?._id : ApiUrl.createVideo;

      if (openModal?._id) {
        await putData(url, formData);
      } else {

        await postData(url, payload, { show, hide });
      }


      const notify = messageService();
      notify.toast("success", openModal?._id ? notify.message.updatedSuccess : notify.message.addedSuccess);

      closeModal();
      resetForm();
      getDataALLs?.();
    } catch (err: any) {
      console.log(err)
      const msg = err?.response?.data?.message || "Something went wrong";
      showAPiError_mesage(msg);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  const uploadFile = async (file: File, type: "image" | "video") => {
    const uploader = type === "image" ? uploadImage : uploadvideo;
    const obj = { [type]: file };
    try {
      const res = await uploader(ApiUrl.fileUpload, obj, { show, hide });
      if (type === "image") {
        setThumbnail(res.original);
        setFormData((prev) => ({ ...prev, thumbnail: res.original }));
        setError((prev) => ({ ...prev, thumbnail: "" }));
      } else {
        setVideoUrl(res.original);
        setFormData((prev) => ({ ...prev, videoUrl: res.original }));
        setError((prev) => ({ ...prev, videoUrl: "" }));
      }
    } catch (err) {
      showAPiError_mesage(`Failed to upload ${type}`);
    }
  };

  const removeFile = (type: "image" | "video") => {
    if (type === "image") {
      setThumbnail("");
      setFormData((prev) => ({ ...prev, thumbnail: "" }));
    } else {
      setVideoUrl("");
      setFormData((prev) => ({ ...prev, videoUrl: "" }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      ariaHideApp={false}
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
    >
      <div className="bg-white w-full max-w-xl rounded-md shadow-lg p-6">
        <div className="flex justify-between items-start">

          <h2 className="text-xl font-bold mb-4">
            {openModal?._id ? "Edit Video Tutorials" : "Add Video Tutorials"}
          </h2>

          <button onClick={closeModal}>
            <X></X>
          </button>
        </div>

        {/* Title */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-2 text-md border border-gray-300 rounded-md  outline-[1px] transition-all duration-500  hover:border-greenBg focus:border-[1px] focus:border-greenBg/50 outline-none focus:shadow-[0px_0px_8px_rgba(0,112,112,0.6)]"
            />
          {error.title && <p className="text-red-500 text-sm">{error.title}</p>}
        </div>

        {/* Thumbnail Upload */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Thumbnail</label>
          {!thumbnail ? (
            <div className="border border-dashed p-4 text-center rounded-md cursor-pointer bg-gray-50">
              <label htmlFor="thumbnail" className="cursor-pointer text-gray-500">
                <ImagePlus className="mx-auto mb-2" /> Upload Thumbnail
              </label>
              <input type="file" id="thumbnail" accept="image/*" className="hidden" onChange={(e) => {
                if (e.target.files?.[0]) uploadFile(e.target.files[0], "image");
              }} />
            </div>
          ) : (
            <div className="relative">
              <img src={thumbnail} alt="thumbnail" className="w-full h-40 object-cover rounded-md" />
              <button
                onClick={() => removeFile("image")}
                className="absolute top-2 right-2 bg-white rounded-full shadow p-1"
              >
                <XCircle className="text-red-500" />
              </button>
            </div>
          )}
          {error.thumbnail && <p className="text-red-500 text-sm">{error.thumbnail}</p>}
        </div>

        {/* Video Upload */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Video</label>
          {!videoUrl ? (
            <div className="border border-dashed p-4 text-center rounded-md cursor-pointer bg-gray-50">
              <label htmlFor="video" className="cursor-pointer text-gray-500">
                <ImagePlus className="mx-auto mb-2" /> Upload Video
              </label>
              <input type="file" id="video" accept="video/*" className="hidden" onChange={(e) => {
                if (e.target.files?.[0]) uploadFile(e.target.files[0], "video");
              }} />
            </div>
          ) : (
            <div className="relative">
              <video src={videoUrl} controls className="w-full rounded-md max-h-56" />
              <button
                onClick={() => removeFile("video")}
                className="absolute top-2 right-2 bg-white rounded-full shadow p-1"
              >
                <XCircle className="text-red-500" />
              </button>
            </div>
          )}
          {error.videoUrl && <p className="text-red-500 text-sm">{error.videoUrl}</p>}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-customOrange text-white rounded-md  hover:bg-greenBg text-sm font-semibold  flex items-center justify-center ease-in-out duration-500 shadow-[1px_2px_2px_rgba(0,0,0,0.3)]"
            >
            {openModal?._id ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default React.forwardRef(AddVideoModal);
